import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import moment from 'moment';
import { ApiService, CoreUiPartialState, getBranch, getUserId, isExternalUser, isProgrammeUser } from '@pos-app/core-ui';
import { CustomersPartialState } from 'apps/pos-app/src/app/features/customers/+state/customers.reducer';
import { TaskGridActionRendererComponent } from '../../taskActionRender/TaskGridActionRendererComponent';
import { FetchJdeOrder } from 'apps/pos-app/src/app/features/orders/+state/orders.actions';
import { CentralPartialState } from '../../../../+state/central.reducer';
import {
  FetchSalesPeopleList,
  FetchSiteList,
  FetchTaskSummary,
  LoadCentralTasks,
  LoadTaskDetails,
  SaveActiveNavIndex,
} from '../../../../+state/central.actions';
import { centralQuery } from '../../../../+state/central.selectors';
import { ordersQuery } from 'apps/pos-app/src/app/features/orders/+state/orders.selectors';

@Component({
  selector: 'app-task-open',
  templateUrl: './task-open.component.html',
  styleUrls: ['./task-open.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenTaskComponent implements OnInit, OnDestroy {
  @ViewChild('content') modalBox: any;

  // Observables and reactive form
  public loading$ = new BehaviorSubject<boolean>(false);
  isBusy$: Observable<boolean>;
  searchTaskForm: FormGroup;

  // Component state variables
  searchValue = '';
  frameworkComponents;
  siteList: any[] = [];
  salesPersonList: any[] = [];
  context;
  rowData = [];
  notificationNavs = [
    { label: 'Assigned To Me', badgeCount: 0 },
    { label: 'My Overdue', badgeCount: 0 },
    { label: 'Updates', badgeCount: 0 },
    { label: 'Unassigned', badgeCount: 0 },
    { label: 'Site Overdue', badgeCount: 0 },
  ];
  activeNavIndex = 0;
  selectedCustomerNumber: any;
  isShowAdvanceFilter = false;

  // Grid-related variables
  private gridApi: GridApi;
  private gridColumnApi: ColumnApi;
  gridOptions: GridOptions;

  // User and task-related variables
  isExternalUser: boolean;
  isProgrammeUser: boolean;
  defaultBranch: string;
  userEmail: string;
  taskSummary: any;
  homeSite: any;
  orderNumber: number;
  orderType: string;
  customerNumber: string;
  loading = false;
  loadingFetchingOrderFromJDE = false;

  // Unsubscribe subject for cleanup
  private unSubscribe$ = new Subject<void>();

  isShowTaskAttachments: boolean;
  entityType: string;
  entityId: string;
  taskDetail: any;

  constructor(
    private store: Store<CentralPartialState>,
    private apiService: ApiService,
    private storeCoreUi: Store<CoreUiPartialState>,
    private router: Router,
    private modalService: NgbModal,
    private customerStore: Store<CustomersPartialState>,
    private location: Location,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.frameworkComponents = {
      actionRenderer: TaskGridActionRendererComponent,
    };
    this.context = { componentParent: this };
    this.gridOptions = this.initializeGridOptions();
    this.orderNumber = this.activatedRoute.snapshot.params.orderNumber;
    this.orderType = this.activatedRoute.snapshot.params.orderType;
    this.customerNumber = this.activatedRoute.snapshot.params.customerNumber;
  }

  ngOnInit() {
    this.initializeStore();
    this.initializePreExistingData();
    this.initializeFormGroup();
    this.validateRouteParams();
    this.subscribeToStoreData();
  }

  // Initialize data from the store
  private initializePreExistingData() {
    this.storeCoreUi.pipe(select(getUserId), takeUntil(this.unSubscribe$)).subscribe((userEmail) => {
      this.userEmail = userEmail;
    });
    this.storeCoreUi.pipe(select(getBranch), takeUntil(this.unSubscribe$)).subscribe((branchData) => {
      this.defaultBranch = branchData?.BranchCode;
    });
    this.storeCoreUi.pipe(select(isExternalUser), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.isExternalUser = data;
    });
    this.storeCoreUi.pipe(select(isProgrammeUser), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.isProgrammeUser = data;
    });

    this.isBusy$ = this.store.select(centralQuery.getIsBusy);
  }

  // Dispatch actions to fetch initial data
  private initializeStore() {
    this.store.dispatch(new FetchSiteList());
    this.store.dispatch(new FetchSalesPeopleList());
  }

  // Update notification navigation badges
  private updateNotificationNavs(data: any) {
    this.notificationNavs[0].badgeCount = data.AssignedToMeTotal || 0;
    this.notificationNavs[1].badgeCount = data.MyOverdueTotal || 0;
    this.notificationNavs[2].badgeCount = data.TicketAcknowledgementTotal || 0;
    this.notificationNavs[3].badgeCount = data.UnassignedTicketsTotal || 0;
    this.notificationNavs[4].badgeCount = data.SiteOverdueTotal || 0;
    this.setActiveNav(this.activeNavIndex);
    this.cdr.detectChanges();
  }

  // Initialize the search form group
  private initializeFormGroup() {
    this.searchTaskForm = new FormGroup({
      searchType: new FormControl(0),
      sourceSite: new FormControl(''),
      assignedSite: new FormControl(''),
      salesPerson: new FormControl(''),
      customerNumber: new FormControl(''),
      assignedTo: new FormControl(''),
      raisedBy: new FormControl(''),
      orderNumber: new FormControl('', [Validators.pattern('^[0-9]*$')]),
    });
  }

  // Validate route parameters and handle navigation
  private validateRouteParams() {
    if ((this.orderNumber && isNaN(this.orderNumber)) || (this.orderType && typeof this.orderType !== 'string')) {
      this.location.back();
    } else if (this.orderNumber && this.orderType) {
      this.isShowAdvanceFilter = true;
      this.showAdvanceFilter();
      this.searchTaskForm.patchValue({ orderNumber: this.orderNumber }, { emitEvent: false });
      this.reloadFilteredTasks();
    } else if (this.customerNumber) {
      this.isShowAdvanceFilter = true;
      this.showAdvanceFilter();
      this.searchTaskForm.patchValue({ customerNumber: this.customerNumber }, { emitEvent: false });
      this.selectedCustomerNumber = this.customerNumber;
      this.reloadFilteredTasks();
    }
  }

  // Subscribe to store data and update component state
  private subscribeToStoreData() {
    this.store.pipe(select(centralQuery.getActiveNavIndex), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.activeNavIndex = data >= 0 ? data : 0;
      if ((this.orderNumber && this.orderType) || this.customerNumber) {
        this.activeNavIndex = -1;
        this.isShowAdvanceFilter = true;
      }

      this.setActiveNav(this.activeNavIndex);
    });

    this.store.pipe(select(centralQuery.getSiteListData), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.siteList = data?.siteList || [];
      this.homeSite = data?.homeSite || null;
    });

    this.store.pipe(select(centralQuery.getSalesPersonList), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.salesPersonList = data || [];
    });

    this.store.pipe(select(centralQuery.getTaskSummaryData), takeUntil(this.unSubscribe$)).subscribe((data) => {
      this.updateTaskSummary(data);
    });
    this.store.dispatch(new LoadCentralTasks());
    this.store.pipe(select(centralQuery.getCentralTaskData)).subscribe((data) => {
      if (data) {
        this.updateNotificationNavs(data);
        this.cdr.detectChanges();
      }
    });

    this.customerStore
      .select(ordersQuery.getIsFetchingJdeOrder)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((x) => (this.loadingFetchingOrderFromJDE = x));
  }

  // Update task summary and grid data
  private updateTaskSummary(data: any) {
    if (data?.tickets?.length > 0) {
      this.rowData = data.tickets.map((ticket) => ({
        ...ticket,
        isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
      }));
      this.updateGridData(this.rowData);
    } else {
      this.clearGridData();
    }
  }

  // Update grid data
  private updateGridData(data: any[]) {
    this.gridApi?.setRowData(data);
    this.gridApi?.paginationGoToPage(0);
  }

  // Clear grid data
  private clearGridData() {
    this.rowData = [];
    this.updateGridData(this.rowData);
  }

  // Reload tasks based on the filtered form values
  private reloadFilteredTasks() {
    const filters = Object.entries(this.searchTaskForm.value).reduce((acc, [key, value]) => {
      if (value !== null && value !== '' && key !== 'searchType') {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (this.searchTaskForm.value.searchType === 2) {
      filters['assignedTo'] = '';
    }

    this.store.dispatch(new FetchTaskSummary(filters));
  }

  // Define grid options
  private initializeGridOptions(): GridOptions {
    return {
      defaultColDef: {
        resizable: true,
        sortable: true,
        width: 150,
        cellClass: 'cell-class ag-right-aligned-cell',
        headerClass: 'header-class ag-left-aligned-header',
        wrapText: true,
        autoHeight: true,
      },
      columnDefs: [
        {
          headerName: 'Order',
          field: 'orderNumber',
          width: 100,
          cellStyle: { 'text-align': 'left', 'text-transform': 'none' },
          cellRenderer: (params) => this.createOrderLink(params),
        },
        { headerName: 'Customer', field: 'CustomerName', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },

        {
          headerName: 'Description',
          field: 'summary',
          flex: 1,
          minWidth: 200,
          cellStyle: { 'text-align': 'left', 'text-transform': 'none', 'white-space': 'normal', 'word-wrap': 'break-word' },
          cellRenderer: (params) => this.createDescriptionLink(params),
        },
        { headerName: 'Status', field: 'status', width: 100, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        {
          headerName: 'Assigned To',
          field: 'assignedToName',
          width: 150,
          cellStyle: (params) => this.getAssignedToCellStyle(params),
        },
        { headerName: 'Assigned Site', field: 'siteAssignedDescription', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        {
          headerName: 'Due Date',
          field: 'dueDate',
          width: 120,
          cellStyle: (params) => this.getDueDateCellStyle(params),
        },
        { headerName: 'Created Date', field: 'createDate', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Requestor', field: 'RaisedByName', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Source Site', field: 'siteRaisedDescription', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Last Changed By', field: 'lastChangedByName', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
        { headerName: 'Last Changed Date', field: 'lastUpdated', width: 150, cellStyle: { 'text-align': 'left', 'text-transform': 'none' } },
      ],
    };
  }

  // Create order link for grid
  private createOrderLink(params) {
    const link = document.createElement('a');
    link.href = 'javascript:void(0)';
    link.innerText = params.value;
    if (params.value > 0) {
      link.addEventListener('click', () => this.fetchOrderFromJDE(params.data));
    } else {
      link.style.pointerEvents = 'none';
      link.style.color = 'grey';
      link.innerText = '';
    }
    return link;
  }

  // Create description link for grid
  private createDescriptionLink(params) {
    const link = document.createElement('a');
    link.href = 'javascript:void(0)';
    link.innerText = params.value;
    if (params.data.priority === 'HIGH') {
      const span = document.createElement('span');
      span.style.color = 'red';
      span.innerText = '! ';
      link.prepend(span);
    }
    link.addEventListener('click', () => this.loadTask(params.data));
    return link;
  }

  // Get cell style for "Assigned To" column
  private getAssignedToCellStyle(params) {
    if (params.value === 'UNASSIGNED') {
      return { 'text-align': 'left', 'text-transform': 'none', 'background-color': '#DE0A21', color: 'white' };
    }
    return { 'text-align': 'left', 'text-transform': 'none' };
  }

  // Get cell style for "Due Date" column
  private getDueDateCellStyle(params) {
    const dueDate = moment(params.value, 'DD/MM/YYYY');
    const currentDate = moment();
    if (dueDate.isSameOrBefore(currentDate, 'day')) {
      return { 'text-align': 'left', 'text-transform': 'none', 'background-color': '#DE0A21', color: 'white' };
    }
    return { 'text-align': 'left', 'text-transform': 'none' };
  }

  // Fetch order details from JDE
  private fetchOrderFromJDE(data) {
    this.customerStore.dispatch(
      new FetchJdeOrder({
        orderNumber: data.orderNumber,
        orderType: data.orderType,
        JDEOrderType: data?.JDEOrderType,
        JDECompany: data?.JDECompany,
      })
    );
  }

  // Load task details and navigate to task detail page
  private loadTask(data) {
    this.store.dispatch(new LoadTaskDetails(data.UKID));
    this.router.navigate(['/central/task/detail', data.UKID]);
  }

  //setActiveNav method to handle navigation
  setActiveNavClick(index) {
    this.router.navigateByUrl('/central/tasks');
    this.setActiveNav(index);
  }

  setActiveNav(index: number): void {
    if (index >= 0) {
      this.isShowAdvanceFilter = false;
      this.clearAdvanceSearch();
    }

    this.activeNavIndex = index;
    if (index === 0 && this.notificationNavs[0].badgeCount > 0) {
      this.apiService
        .listTasks({
          returnSiteTasksTF: false,
          returnClosedTasksTF: false,
          returnOverdueOnlyTF: false,
          returnAcknowledgementOnlyTF: false,
          returnUnassignedOnlyTF: false,
        })
        .subscribe((data: any) => {
          this.loading = false;

          if (data.Tickets) {
            this.taskSummary = data.Tickets || [];
            this.loading = false;
            this.rowData = this.taskSummary.map((data) => ({
              ...data,
              isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
            }));
            this.gridApi?.setRowData(this.rowData);
            this.gridApi?.paginationGoToPage(0);
          }
        });
    } else if (index === 1 && this.notificationNavs[1].badgeCount > 0) {
      this.apiService
        .listTasks({
          returnSiteTasksTF: false,
          returnClosedTasksTF: false,
          returnOverdueOnlyTF: true,
          returnAcknowledgementOnlyTF: false,
          returnUnassignedOnlyTF: false,
        })
        .subscribe((data: any) => {
          this.loading = false;
          if (data.Tickets) {
            this.taskSummary = data.Tickets || [];
            this.loading = false;
            this.rowData = this.taskSummary.map((data) => ({
              ...data,
              isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
            }));
            this.gridApi?.setRowData(this.rowData);
            this.gridApi?.paginationGoToPage(0);
          }
        });
    } else if (index === 2 && this.notificationNavs[2].badgeCount > 0) {
      this.apiService
        .listTasks({
          returnSiteTasksTF: false,
          returnClosedTasksTF: false,
          returnOverdueOnlyTF: false,
          returnAcknowledgementOnlyTF: true,
          returnUnassignedOnlyTF: false,
        })
        .subscribe((data: any) => {
          this.taskSummary = data.Tickets || [];
          this.loading = false;
          this.rowData = this.taskSummary.map((data) => ({
            ...data,
            isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
          }));
          this.gridApi?.setRowData(this.rowData);
          this.gridApi?.paginationGoToPage(0);
        });
    } else if (index === 3 && this.notificationNavs[3].badgeCount > 0) {
      this.apiService
        .listTasks({
          returnSiteTasksTF: true,
          returnClosedTasksTF: false,
          returnOverdueOnlyTF: false,
          returnAcknowledgementOnlyTF: false,
          returnUnassignedOnlyTF: true,
        })
        .subscribe((data: any) => {
          this.taskSummary = data.Tickets || [];
          this.loading = false;
          this.rowData = this.taskSummary.map((data) => ({
            ...data,
            isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
          }));
          this.gridApi?.setRowData(this.rowData);
          this.gridApi?.paginationGoToPage(0);
        });
    } else if (index === 4 && this.notificationNavs[4].badgeCount > 0) {
      this.apiService
        .listTasks({
          returnSiteTasksTF: true,
          returnClosedTasksTF: false,
          returnOverdueOnlyTF: true,
          returnAcknowledgementOnlyTF: false,
          returnUnassignedOnlyTF: false,
        })
        .subscribe((data: any) => {
          this.taskSummary = data.Tickets || [];
          this.loading = false;
          this.rowData = this.taskSummary.map((data) => ({
            ...data,
            isExternalOrProgrammeUser: this.isExternalUser || this.isProgrammeUser,
          }));
          this.gridApi?.setRowData(this.rowData);
          this.gridApi?.paginationGoToPage(0);
        });
    }
  }
  showAdvanceFilter() {
    this.isShowAdvanceFilter = !this.isShowAdvanceFilter;
    if (this.isShowAdvanceFilter) {
      this.setActiveNav(-1);
    } else {
      this.activeNavIndex = 0;
      this.setActiveNav(this.activeNavIndex);
    }
    this.clearAdvanceSearch();

    //this.updateFormValues();
    //this.reloadFilteredTasks();
  }

  // Placeholder for search task functionality
  searchTask() {
    this.reloadFilteredTasks();
  }

  // Clear advanced search form fields
  clearAdvanceSearch() {
    this.searchTaskForm?.patchValue(
      {
        sourceSite: '',
        assignedSite: '',
        customerNumber: '',
        orderNumber: '',
        raisedBy: '',
      },
      { emitEvent: true }
    );
    this.selectedCustomerNumber = '';
    this.clearGridData();
  }

  // Handle grid ready event
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    const sortModel = [{ colId: 'dateTime', sort: 'desc' }];
    this.gridApi.resetRowHeights();
  }

  // Handle modal close event
  handleCloseModal(event) {
    if (event) {
      this.modalService.dismissAll();
    }
  }
  selectCustomer(e) {
    if (e) {
      this.selectedCustomerNumber = e.customerNumber.toString();
      this.searchTaskForm.patchValue({ customerNumber: Number(e.customerNumber) }, { emitEvent: false });
    }
  }

  ngOnDestroy() {
    this.store.dispatch(new SaveActiveNavIndex(this.activeNavIndex));
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
